'use client';

import { MagnifyingGlass, X } from 'phosphor-icons';
import { FormEvent, useRef } from 'react';
import { c } from 'ui-components';
import { useSearchHotkey } from '../hooks/useSearchHotkey';
import { StandaloneHeaderSearchProps } from './HeaderSearch.types';
import { SearchHotkeyHint } from './SearchHotkeyHint';
import { navigateToResults, searchUrlForQuery } from './searchRedirect';

/**
 * Plain header search form — no Coveo engine, no suggestions, always working.
 * It is what renders while the header engine bootstraps and what stays on
 * screen if that bootstrap never succeeds, so the header is never a disabled
 * or dead box.
 *
 * Markup, styling and data-test hooks match the engine-backed header so the
 * swap is invisible and both share the e2e selectors.
 */
export const StandaloneHeaderSearch = ({
  placeholder,
  navigate = navigateToResults,
  value,
  onValueChange,
  onFocusChange,
  className = '',
}: StandaloneHeaderSearchProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { shortcutKeys, ariaKeyShortcuts } = useSearchHotkey({ inputRef });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!value.trim()) {
      return;
    }
    navigate(searchUrlForQuery(value));
  };

  const handleClear = () => {
    onValueChange('');
    inputRef.current?.focus();
  };

  return (
    <form
      onSubmit={handleSubmit}
      role="search"
      data-test="header-search-form"
      className={className}
    >
      <fieldset className="group flex items-stretch overflow-hidden rounded-md border border-grey-600 bg-grey-900 focus-within:border-transparent focus-within:bg-grey-800 focus-within:ring-2 focus-within:ring-blue-hover">
        <legend className="sr-only">{placeholder}</legend>
        <div className="relative flex flex-1 items-center">
          <input
            onChange={(e) => onValueChange(e.target.value)}
            onFocus={() => onFocusChange?.(true)}
            onBlur={() => onFocusChange?.(false)}
            value={value}
            type="text"
            placeholder={placeholder}
            aria-label={placeholder}
            aria-keyshortcuts={ariaKeyShortcuts}
            className="block w-full bg-transparent px-3 py-2 font-medium text-white placeholder:text-grey-400 focus:outline-none"
            ref={inputRef}
            autoComplete="off"
          />
          <SearchHotkeyHint keys={shortcutKeys} hidden={value.length > 0} />
          <button
            type="button"
            className={c('mr-2 shrink-0 p-1 text-grey-400 hover:text-white', {
              hidden: value.length === 0,
            })}
            onClick={handleClear}
            aria-label="Clear search"
          >
            <X height={15} width={15} />
          </button>
        </div>
        <button
          type="submit"
          className="flex w-10 shrink-0 items-center justify-center bg-blue hover:bg-blue-hover"
          aria-label="Search"
        >
          <MagnifyingGlass width={16} height={16} className="text-white" />
        </button>
      </fieldset>
    </form>
  );
};
